import React, { useEffect, useMemo, useState } from "react";
import { GetDailyBusinessReport } from "adminApi/BookingReport/BookingReportApi";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fieldsConfig from "../json/DailyBusinessReportFields.json";

const toNumber = (val) => {
  if (val === null || val === undefined || val === "" || val === "-") return 0;
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
};

// Safe helper to sum numerical columns (supports comma-separated values like "1500, 1200")
const sumColumn = (rows, key) => {
  let total = 0;
  let hasValid = false;

  rows.forEach((r) => {
    const val = r[key];
    if (val !== null && val !== undefined && val !== "" && val !== "-") {
      const parts = String(val).split(",");
      parts.forEach((p) => {
        const num = Number(p.trim());
        if (Number.isFinite(num)) {
          total += num;
          hasValid = true;
        }
      });
    }
  });

  if (!hasValid) return "-";
  return Number.isInteger(total) ? String(total) : total.toFixed(2);
};

const DailyBusinessReportTable = ({ filters, date }) => {
  const [search, setSearch] = useState("");
  const [rawData, setRawData] = useState([]);
  const [taxNames, setTaxNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedSearch = useDebouncedValue(search, 400);

  // Fetch report whenever checkin date changes
  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const checkinDate = filters.checkin_date || "";
        const res = await GetDailyBusinessReport(
          { checkinDate },
          controller.signal
        );

        if (res?.success) {
          const rows = res.rows || res.data?.rows || [];
          const taxes = res.taxNames || res.data?.taxNames || [];
          setRawData(rows);
          setTaxNames(taxes);
        } else {
          setError(res?.message || "Failed to fetch daily business report");
          setRawData([]);
          setTaxNames([]);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Something went wrong fetching report");
          setRawData([]);
          setTaxNames([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [filters.checkin_date]);

  // Compute Summary Totals for top cards
  const totals = useMemo(() => {
    if (!rawData || rawData.length === 0) return null;

    let totalRevenue = 0;
    let totalCash = 0;
    let totalBank = 0;
    let totalTax = 0;
    let totalVehicles = 0;
    let totalExtraCharges = 0;

    rawData.forEach((row) => {
      totalRevenue += toNumber(row["Totle"]);
      totalCash += toNumber(row["Cash Amount"]);
      totalBank += toNumber(row["Bank Amount"]);
      totalTax += toNumber(row["Tax Amount"]);
      totalVehicles += toNumber(row["Park"]);
      totalExtraCharges +=
        toNumber(row["Person Charge"]) +
        toNumber(row["Child Charge"]) +
        toNumber(row["Bed Charge"]);
    });

    return {
      totalBookings: rawData.length,
      totalRevenue: totalRevenue.toFixed(2),
      totalCash: totalCash.toFixed(2),
      totalBank: totalBank.toFixed(2),
      totalTax: totalTax.toFixed(2),
      totalVehicles,
      totalExtraCharges: totalExtraCharges.toFixed(2),
    };
  }, [rawData]);

  // Client-side search across all columns
  const filteredData = useMemo(() => {
    if (!debouncedSearch) return rawData;
    const query = debouncedSearch.toLowerCase().trim();
    return rawData.filter((row) =>
      Object.values(row).some((val) =>
        String(val ?? "")
          .toLowerCase()
          .includes(query)
      )
    );
  }, [rawData, debouncedSearch]);

  // Production-level column ordering:
  // SR -> Bill No -> Booking No -> Date -> Nights -> Guest Name -> Room Type -> Room No -> Guests ->
  // Room Charge -> Beds -> Price Per Bed -> Extra Person -> Person Charge -> Extra Child -> Child Charge ->
  // Extra Bed -> Extra Bed Charge -> Parking -> Dynamic Taxes (GST, SGST...) -> Total ->
  // Payment Method -> Payment Date -> Cash -> Bank -> Transaction ID
  const displayFields = useMemo(() => {
    const preTax = fieldsConfig.preTaxFields || [];
    const postTax = fieldsConfig.postTaxFields || [];

    const dynamicTaxFields = (taxNames || []).map((tax) => ({
      name: tax,
      label: String(tax).toUpperCase(),
      type: "text",
    }));

    return generateDisplayFields([...preTax, ...dynamicTaxFields, ...postTax]);
  }, [taxNames]);

  // Build the bottom Total row object for the table
  const footerRow = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return null;

    const row = {
      rowNo: "Total",
      SR: "Total",
      "BILL NO": "-",
      "Bill No": "-",
      "BOOKING NO": `${filteredData.length} Bookings`,
      "Booking No": `${filteredData.length} Bookings`,
      DATE: "-",
      Date: "-",
      NIGHTS: sumColumn(filteredData, "No Of Days"),
      "No Of Days": sumColumn(filteredData, "No Of Days"),
      "GUEST NAME": "-",
      Name: "-",
      "ROOM TYPE": "-",
      "Room Type": "-",
      "ROOM NO": "-",
      "Room No": "-",
      GUESTS: sumColumn(filteredData, "No Of People"),
      "No Of People": sumColumn(filteredData, "No Of People"),
      "ROOM CHARGE": sumColumn(filteredData, "Room Price"),
      "Room Price": sumColumn(filteredData, "Room Price"),
      BEDS: sumColumn(filteredData, "No Of Beds"),
      "No Of Beds": sumColumn(filteredData, "No Of Beds"),
      "PRICE PER BED": sumColumn(filteredData, "Price Per Bed"),
      "Price Per Bed": sumColumn(filteredData, "Price Per Bed"),
      "EXTRA PERSON": sumColumn(filteredData, "Extra Person"),
      "Extra Person": sumColumn(filteredData, "Extra Person"),
      "PERSON CHARGE": sumColumn(filteredData, "Person Charge"),
      "Person Charge": sumColumn(filteredData, "Person Charge"),
      "EXTRA CHILD": sumColumn(filteredData, "Extra Child"),
      "Extra Child": sumColumn(filteredData, "Extra Child"),
      "CHILD CHARGE": sumColumn(filteredData, "Child Charge"),
      "Child Charge": sumColumn(filteredData, "Child Charge"),
      "EXTRA BED": sumColumn(filteredData, "Extra Bed"),
      "Extra Bed": sumColumn(filteredData, "Extra Bed"),
      "EXTRA BED CHARGE": sumColumn(filteredData, "Bed Charge"),
      "Bed Charge": sumColumn(filteredData, "Bed Charge"),
      PARKING: sumColumn(filteredData, "Park"),
      Park: sumColumn(filteredData, "Park"),
      TOTAL: sumColumn(filteredData, "Totle"),
      Totle: sumColumn(filteredData, "Totle"),
      "PAYMENT METHOD": "-",
      "Payment Method": "-",
      "PAYMENT DATE": "-",
      "Date Of Payment": "-",
      CASH: sumColumn(filteredData, "Cash Amount"),
      "Cash Amount": sumColumn(filteredData, "Cash Amount"),
      BANK: sumColumn(filteredData, "Bank Amount"),
      "Bank Amount": sumColumn(filteredData, "Bank Amount"),
      "TRANSACTION ID": "-",
      "Transaction Id": "-",
    };

    (taxNames || []).forEach((tax) => {
      const sum = sumColumn(filteredData, tax);
      row[tax] = sum;
      row[String(tax).toUpperCase()] = sum;
    });

    return row;
  }, [filteredData, taxNames]);

  // Configure DataTable
  const { formattedData, columns } = useTableConfig({
    data: filteredData,
    canShowAction: false,
    refetch: () => { },
    IsDelete: false,
    IsEdit: false,
    config: {
      entityName: "Daily Bill Report",
      editUrl: "",
      idKey: "Booking No",
      deleteApi: () => { },
      displayFields,
    },
  });

  // Ensure first column header is named "SR" as in production report
  const productionColumns = useMemo(() => {
    return columns.map((col) => {
      if (col.accessor === "rowNo") {
        return {
          ...col,
          Header: "SR",
        };
      }
      return col;
    });
  }, [columns]);

  const handleSearch = (val) => {
    setSearch(val);
  };

  return (
    <>
      {totals && (
        <div className="row g-2 mb-3">
          {/* <SummaryCard
            label="Total Bookings"
            value={totals.totalBookings}
            icon="bi-journal-check"
            color="primary"
          /> */}
          <SummaryCard
            label="Total Revenue"
            value={`₹${totals.totalRevenue}`}
            icon="bi-currency-rupee"
            color="success"
          />
          <SummaryCard
            label="Cash Amount"
            value={`₹${totals.totalCash}`}
            icon="bi-cash-coin"
            color="warning"
          />
          <SummaryCard
            label="Bank Amount"
            value={`₹${totals.totalBank}`}
            icon="bi-bank"
            color="info"
          />
          <SummaryCard
            label="Tax Amount"
            value={`₹${totals.totalTax}`}
            icon="bi-receipt"
            color="danger"
          />
          <SummaryCard
            label="Extra Charges"
            value={`₹${totals.totalExtraCharges}`}
            icon="bi-plus-circle"
            color="secondary"
          />
          {/* <SummaryCard
            label="Vehicles Parked"
            value={totals.totalVehicles}
            icon="bi-car-front"
            color="dark"
          /> */}
        </div>
      )}

      <DataTable
        columns={productionColumns}
        data={formattedData}
        options={{
          loading,
          error,
          sortableColumns: displayFields.map((f) => f.name),
          copy: true,
          csv: true,
          pdf: true,
          print: true,
          fileName: `Daily_Business_Report_${filters.checkin_date || "today"}`,
          footerRow,
        }}
        Search={{
          search: true,
          SearchInputValue: search,
          handleSearch: handleSearch,
        }}
        pagination={{
          isActive: false,
        }}
        isDate={true}
        date={date}
      />
    </>
  );
};

// Very compact SummaryCard styled to col-lg-2
const SummaryCard = ({ label, value, icon, color = "primary" }) => (
  <div className="col-xl-2 col-lg-2 col-md-3 col-sm-4 col-6">
    <div className="card border bg-white shadow-none h-100 rounded-2">
      <div className="card-body p-2 d-flex flex-column justify-content-between">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <span
            className="text-uppercase text-secondary fw-semibold text-truncate"
            style={{ fontSize: "10.5px", letterSpacing: "0.4px" }}
            title={label}
          >
            {label}
          </span>
          {icon && (
            <i
              className={`bi ${icon} text-${color} opacity-75`}
              style={{ fontSize: "14px" }}
            ></i>
          )}
        </div>
        <div className="d-flex align-items-baseline justify-content-between">
          <span className="fw-bold text-dark fs-6 text-truncate" title={value}>
            {value}
          </span>
        </div>
      </div>
    </div>
  </div>
);

export default DailyBusinessReportTable;
